import React, { useCallback, useMemo, useState } from "react";

import { TabPanel } from "@mui/lab";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useSearchParams } from "react-router-dom-v5-compat";
import { SetURLSearchParams } from "react-router-dom-v5-compat/dist/react-router-dom";

import useGetPortfolioPlans from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlans";
import { PortfolioPlanParams } from "@fartherfinance/frontend/api/Investment/types";

import useIsAdmin from "../../utils/useIsAdmin";
import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { TableSortOrder } from "@src/sharedComponents/Table/Types";
import { State } from "@src/store";

import CreateCustomOrPartnerModelModal, {
  type NewModelDetails,
} from "./CreateCustomOrPartnerModelModal/CreateCustomOrPartnerModelModal";
import CreateCustomModelModal from "./CreateCustomPortfolioModal/CreateCustomModelModal";
import CSVUploadModalLauncher from "./CreatePortfolioUploadModal/ModalLauncher";
import { ModelDetails } from "./PortfolioBuilder/reducer/Types";
import {
  isSortOrder,
  isTableKey,
  TableKey,
} from "./PortfolioPlanTab/PortfolioData";
import { PortfolioPlanTable } from "./PortfolioPlanTab/PortfolioPlanTable";
import {
  ModalType,
  TableHeader,
} from "./PortfolioPlanTab/PortfolioTableHeader";

import styles from "./PortfolioPlanTab/PortfolioPlan.module.css";

const tabPanelValue = `/Advisor/${InvestmentsPath}/ModelMarketplaceV2`;

export const usePortfolioPlanQueryParam = (): {
  params: URLSearchParams;
  setParams: SetURLSearchParams;
  queryParams: PortfolioPlanParams;
} => {
  const [params, setParams] = useSearchParams({
    page: "1",
    pageSize: "10",
  });

  const queryParams = useMemo(
    () =>
      ({
        $expand: ["assignmentCount", "provider"],
        ...(params.get("search") && { search: params.get("search") }),
        ...(params.get("page") && { page: params.get("page") }),
        ...(params.get("pageSize") && { pageSize: params.get("pageSize") }),
        ...(params.get("orderBy") && { orderBy: params.get("orderBy") }),
      } as PortfolioPlanParams),
    [params]
  );

  return {
    params,
    setParams,
    queryParams,
  };
};

const PortfolioPlanList: React.FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  const { params, queryParams, setParams } = usePortfolioPlanQueryParam();
  const openModalOnLoad = params.get("openModal");
  const [showCSVUploadModal, setShowCSVUploadModal] = useState<boolean>(false);
  const [showCreateModelModal, setShowCreateModelModal] = useState<boolean>(
    Boolean(openModalOnLoad)
  );

  const closeCSVUploadModal = () => setShowCSVUploadModal(false);
  const closeCreateModelModal = () => setShowCreateModelModal(false);

  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
  }));

  const { data: isAdmin } = useIsAdmin(advisorId);

  const history = useHistory();

  const auth = useAdvisorRequestAuth();

  const portfolioData = useGetPortfolioPlans(queryParams, auth);

  const handlePageChange = useCallback(
    (page: number) => setParams({ ...queryParams, page: page.toString() }),
    [queryParams, setParams]
  );

  const handleOrderByChange = useCallback(
    (sortBy: TableKey, sortOrder: TableSortOrder) => {
      const orderBy = `${sortBy} ${sortOrder}` as const;
      setParams({ ...queryParams, orderBy });
    },
    [queryParams, setParams]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setParams({
        ...queryParams,
        search: e.target.value,
        page: "1",
      });
    },
    [queryParams, setParams]
  );

  const handleOpenModal = useCallback(
    (modalType: ModalType) => {
      setParams({
        ...queryParams,
      });

      if (modalType === "upload") {
        setShowCSVUploadModal(true);
      } else {
        setShowCreateModelModal(true);
      }
    },
    [queryParams, setParams]
  );

  const handleFilterChange = useCallback(
    (filter: { id: string; label: string }) => {
      setParams({
        ...queryParams,
        filterBy: filter.label,
      });
    },
    [queryParams, setParams]
  );

  const getOrderBy: [TableKey, TableSortOrder] | null = useMemo(() => {
    const orderBy = params.get("orderBy");
    if (orderBy === null) {
      return null;
    }
    // take this string and make sure that we have a valid order by
    const [column, direction] = orderBy.split(" ");

    if (!isTableKey(column) || !isSortOrder(direction)) {
      return null;
    }

    return [column, direction];
  }, [params]);

  const onEnterCreateCustomPortfolio = (modelDetails: ModelDetails) => {
    setShowCreateModelModal(false);
    history.push(
      `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/Create?modelName=${modelDetails.modelName}&taxType=${modelDetails.taxType}&basePermission=${modelDetails.basePermission}`,
      history.location.state
    );
  };

  const onEnterCreateCustomOrPartnerPortfolio = ({
    modelName,
    firm,
    series,
    allocation,
    taxType,
    basePermission,
  }: NewModelDetails) => {
    if (
      firm === undefined ||
      series === undefined ||
      allocation === undefined
    ) {
      onEnterCreateCustomPortfolio({
        modelName,
        taxType,
        basePermission: "Private", // Advisors/non-admins can only create private models
      });
      return;
    }

    setShowCreateModelModal(false);

    history.push(
      `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/Create?modelName=${modelName}&firm=${firm}&series=${series}&allocation=${allocation}&taxType=${taxType}&basePermission=${basePermission}`,
      history.location.state
    );
  };

  return (
    <TabPanel className={styles.tabPanel} value={tabPanelValue}>
      <TableHeader
        input={params.get("search") ?? ""}
        onInputChange={handleInputChange}
        onCreateModelPress={handleOpenModal}
        onFilterChange={handleFilterChange}
      />

      <PortfolioPlanTable
        data={portfolioData.data}
        isLoading={portfolioData.isLoading}
        hasError={portfolioData.hasError}
        onPageChange={handlePageChange}
        onSorterChange={handleOrderByChange}
        orderBy={getOrderBy}
      />

      {children}

      <CSVUploadModalLauncher
        showModal={showCSVUploadModal}
        closeModal={closeCSVUploadModal}
      />

      {!isAdmin && showCreateModelModal && (
        <CreateCustomModelModal
          isOpen={showCreateModelModal}
          onClose={closeCreateModelModal}
          onSubmit={onEnterCreateCustomPortfolio}
        />
      )}

      {isAdmin && showCreateModelModal && (
        <CreateCustomOrPartnerModelModal
          isOpen={showCreateModelModal}
          onClose={closeCreateModelModal}
          onSubmit={onEnterCreateCustomOrPartnerPortfolio}
        />
      )}
    </TabPanel>
  );
};

export default PortfolioPlanList;
